import React from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { createTheme, ThemeProvider } from "@mui/material";
import * as msal from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import {
    QueryClientProvider, QueryClient, QueryCache, MutationCache,
} from "@tanstack/react-query";
import i18n, { initialize as i18nInitialize } from "./i18n";
import { themeMUI } from "./styles/themeMUI";
import { CLIENT_ID, MSAL_AUTHORITY_UK, MSAL_AUTHORITY_US } from "./services/serviceConfig";
import { getPathnameBeforeAuth } from "./hooks/useRedirectAfterAuth";
import App from "./App";
import { isVersionUK, isVersionUS } from "./services/versionService";
import { ToastProvider } from "./context/toast/ToastContext";
import { appTrace } from "./services/appTrace";
import { initializeApplicationInsights } from "./services/applicationInsights";
import "./styles/index.css";

i18nInitialize();
initializeApplicationInsights();

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            appTrace.error(error, {
                source: "react_query.query",
                queryKey: JSON.stringify(query.queryKey),
            });
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            appTrace.error(error, {
                source: "react_query.mutation",
                mutationKey: JSON.stringify(mutation.options.mutationKey),
            });
        },
    }),
    defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const msalInstance = new msal.PublicClientApplication({
    auth: {
        clientId: CLIENT_ID,
        authority: isVersionUK ? MSAL_AUTHORITY_UK : MSAL_AUTHORITY_US,
        redirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: true,
    },
});

if (isVersionUS) {
    document?.body?.setAttribute("data-uspa", "true");
}

const container = document.getElementById("root");
if (!container) throw new Error("Root container #root not found");

const root = createRoot(container);

const renderApp = async () => {
    await msalInstance.initialize();
    const impersonationMatch = window?.location?.pathname?.match(/^\/impersonate\/(.+)$/);
    const impersonationToken = impersonationMatch?.[1];

    if (impersonationToken) {
        sessionStorage.setItem("tokenImpersonate", impersonationToken);
        window.history.replaceState({}, "", "/");
    }

    const response = await msalInstance.handleRedirectPromise();

    if (response?.account) {
        msalInstance.setActiveAccount(response.account);
    } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
        }
    }

    getPathnameBeforeAuth();

    root.render(
        <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
                <I18nextProvider i18n={i18n}>
                    <ThemeProvider theme={createTheme(themeMUI)}>
                        <ToastProvider>
                            <App />
                        </ToastProvider>
                    </ThemeProvider>
                </I18nextProvider>
            </QueryClientProvider>
        </MsalProvider>,
    );
};

renderApp().catch((error) => {
    appTrace.error(error, {
        source: "render_app",
    });
});
